import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, ScrollText, Layers } from "lucide-react";
import { PageHero, Section, Eyebrow, H2, P } from "@/components/SharedUI";

const CIRCULAR_STACK = "https://customer-assets-wrfwihn1.emergentagent.net/job_bigibila-react/artifacts/tlf9v00e_image.png";

const signals = [
  "Sacred places",
  "Lore",
  "Ceremony",
  "Kinship systems",
  "Oral traditions",
  "Colonial archives",
  "Contemporary Indigenous governance evidence",
];

const domains = [
  ["Country", "Land · Water · Sky"],
  ["Lore / Dreaming", "Law · Stories · Songlines"],
  ["Language", "Dialect · Song · Story — the connector"],
  ["Kinship & Totems", "Skin Names · Mobs · Totemic Links"],
  ["Customs & Practice", "Ceremony · Art · Dance · Knowledge Sharing"],
];

export default function Research() {
  return (
    <>
      <PageHero
        eyebrow="Bigibila Research · Governance Archaeology"
        title="Can Aboriginal governance be identified through deep-time observations?"
        desc="A research project asking whether First Nations governance systems can be recognised through the signals already present in public evidence — sacred places, Lore, ceremony, kinship, oral traditions, colonial archives and contemporary Indigenous governance evidence — mapped into a First Nations Governance Architecture, without interpreting or claiming ownership of cultural knowledge."
      />

      {/* THE RESEARCH QUESTION */}
      <section className="border-b border-stone-200 bg-[#FAFAF9]">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <Eyebrow>The research question</Eyebrow>
              <H2 className="text-3xl md:text-4xl">One question. Public evidence. Deep time.</H2>
            </div>
            <div className="lg:col-span-8">
              <div className="border-l-4 border-[#9C4221] pl-6 py-2" data-testid="research-question">
                <p className="font-display italic text-2xl md:text-3xl leading-snug tracking-tight text-stone-900 max-w-3xl">
                  Can Aboriginal governance systems be identified through governance signals contained within sacred places, Lore, ceremony, kinship systems, oral traditions, colonial archives and contemporary Indigenous governance evidence?
                </p>
                <div className="mt-6 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500">
                  Bigibila Research · Instructions!B3
                </div>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* THE ILLUSTRATION + DOMAINS */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="mb-12 max-w-3xl">
            <Eyebrow>The architecture</Eyebrow>
            <H2 className="text-3xl md:text-4xl">First Nations Governance Architecture — read as one whole.</H2>
            <P className="mt-6">
              Country holds everything. Language sits at the centre as the connector — the voice of the land where Lore is stored and spoken. Every signal from the public record maps to one of the five governance domains, without ever asking anyone to disclose cultural knowledge to prove it.
            </P>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="bg-[#FAFAF9] border border-stone-200 p-3 md:p-6">
                <img
                  src={CIRCULAR_STACK}
                  alt="Bigibila Governance Stack — Country, Lore/Dreaming, Language, Kinship & Totems, Customs & Practice"
                  className="w-full h-auto"
                  data-testid="research-stack-illustration"
                />
              </div>
              <p className="mt-4 text-[11px] italic text-stone-500 max-w-md">
                Bigibila Governance Stack — the five architectural governance domains, with Language at the centre.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-3" data-testid="research-domains">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-3">
                Architectural governance domains
              </div>
              {domains.map(([name, sub]) => (
                <div key={name} className="border-l-4 border-[#9C4221] pl-5 py-2 bg-[#FAFAF9]">
                  <div className="font-display text-lg font-bold text-stone-900 tracking-tight uppercase">{name}</div>
                  <div className="text-[12px] text-stone-500 italic mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* SIGNALS */}
      <section className="border-b border-stone-200 bg-[#FAFAF9]">
        <Section>
          <div className="mb-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Eyebrow>Signals we map</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Public evidence — never private disclosure.</H2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-stone-700 leading-relaxed">
                We map governance signals from what is already public. Each signal is tested for whether it reveals a governance function in the architecture.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="research-signals">
            {signals.map((s, i) => (
              <div key={s} className="bg-white border border-stone-200 p-5">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221]">
                  Signal {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 text-[15px] font-bold text-stone-900 leading-tight">{s}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* SAFEGUARD */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>The safeguard</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Cultural authority stays with the people it belongs to.</H2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-stone-700 leading-relaxed">
              <p>
                The research uses <span className="text-stone-900 font-medium">public evidence only</span> — no private disclosures, no ceremony content, no restricted knowledge, no attempts to reconstruct what belongs behind Lore.
              </p>
              <p>
                Cultural authority remains where it always has: with the relevant <span className="text-[#9C4221] font-medium">Traditional Owners, Elders, families and Nations</span>. Where a signal belongs to a Nation or family other than our own, we say so and we refer it.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
                {[
                  [Shield, "Public evidence only"],
                  [ScrollText, "No cultural knowledge disclosed"],
                  [Layers, "Authority stays with Traditional Owners"],
                ].map(([Icon, label]) => (
                  <div key={label} className="border border-stone-200 bg-[#FAFAF9] p-5">
                    <Icon className="h-4 w-4 text-[#9C4221]" strokeWidth={1.5} />
                    <div className="mt-3 text-[13px] font-bold text-stone-900 leading-tight">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* WHAT IT DOES NOT DO + CTA */}
      <section className="border-b border-stone-200 bg-[#FAFAF9]">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>What it does not do</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Restraint is the research.</H2>
            </div>
            <div className="lg:col-span-7 space-y-3 text-stone-700 leading-relaxed">
              {[
                "Does not interpret cultural knowledge — only observes governance function.",
                "Does not claim ownership of any Nation's Lore, Language or governance.",
                "Does not publish the evidence register — zero of its records are cleared for public release.",
                "Does not train external AI systems on cultural material.",
              ].map((t) => (
                <p key={t} className="flex gap-3">
                  <span className="text-[#9C4221] font-bold flex-none">✕</span>
                  <span>{t}</span>
                </p>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* CTA */}
      <section>
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Commission the research</Eyebrow>
            <H2 className="text-3xl md:text-4xl">Bring one Nation, one project, one question.</H2>
            <P className="mt-4">
              Governance Archaeology engagements are scoped by Nation and by evidence stream. Fixed-fee for a defined register; day rate for ongoing coding.
            </P>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              data-testid="research-cta"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-7 py-4 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
            >
              Book a scoping call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      </section>
    </>
  );
}
